<template>
  <div class="login-body">
    <!-- 左：品牌视觉区 -->
    <div class="login-visual">
      <div class="decor-blur decor-blur--1"></div>
      <div class="decor-blur decor-blur--2"></div>

      <div class="visual-top">
        <div class="visual-brand">
          <span class="brand-dot"></span>
          <span class="visual-brand-name">青鸾</span>
        </div>
        <p class="visual-poem">蓬山此去无多路，<br>青鸟殷勤为探看。</p>
      </div>

      <div class="visual-characters">
        <AnimatedCharacters
          :is-typing="isTyping"
          :show-password="showPassword"
          :password-length="passwordLength"
        />
      </div>

      <p class="visual-footer">XCPC 效能工具</p>
    </div>

    <!-- 中：表单区 -->
    <div class="login-form-side">
      <div class="form-wrapper">
        <div class="form-header">
          <h1 class="form-title">登录</h1>
          <p class="form-subtitle">登录系统，管理你的训练数据与竞赛历程</p>
        </div>

        <div class="mode-switch">
          <button :class="['mode-btn', { active: activeTab === 'password' }]" @click="activeTab = 'password'">密码登录</button>
          <button :class="['mode-btn', { active: activeTab === 'totp' }]" @click="activeTab = 'totp'">动态码登录</button>
        </div>

        <form v-if="activeTab === 'password'" @submit.prevent="loginWithPassword" class="login-form">
          <div class="field">
            <label class="field-label">账号</label>
            <el-input v-model="passwordForm.username" placeholder="账号或真实姓名" size="large"
                      @keyup.enter="loginWithPassword" @focus="isTyping = true" @blur="isTyping = false"/>
          </div>
          <div class="field">
            <label class="field-label">密码</label>
            <el-input v-model="passwordForm.password" :type="showPassword ? 'text' : 'password'"
                      placeholder="请输入密码" size="large" @keyup.enter="loginWithPassword"
                      @input="onPasswordInput">
              <template #suffix>
                <el-icon class="pwd-toggle" @click="showPassword = !showPassword">
                  <View v-if="showPassword" /><Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </div>
          <div class="form-options">
            <el-checkbox v-model="rememberMe">记住账号</el-checkbox>
          </div>
          <el-button type="primary" native-type="submit" :loading="loading" class="submit-btn" size="large">
            {{ loading ? '登录中…' : '登录' }}
          </el-button>
        </form>

        <form v-else @submit.prevent="loginWithTotp" class="login-form">
          <div class="field">
            <label class="field-label">账号</label>
            <el-input v-model="totpForm.username" placeholder="请输入账号" size="large" @keyup.enter="loginWithTotp"/>
          </div>
          <div class="field">
            <label class="field-label">动态码</label>
            <el-input v-model="totpForm.code" placeholder="6位动态码" maxlength="6" size="large" @keyup.enter="loginWithTotp"/>
          </div>
          <el-button type="primary" native-type="submit" :loading="loading" class="submit-btn" size="large">
            {{ loading ? '登录中…' : '登录' }}
          </el-button>
        </form>

        <div class="register-entry">
          还没有账号？<router-link to="/register">立即注册</router-link>
        </div>
      </div>
    </div>

    <!-- 右：功能介绍 -->
    <div class="login-features">
      <div class="features-header">
        <h2 class="features-title">我们能做什么</h2>
        <p class="features-subtitle">集训队训练管理平台</p>
      </div>

      <div class="feature-grid">
        <div class="feature-card" v-for="f in features" :key="f.title">
          <div class="feature-icon-box">
            <el-icon class="feature-icon"><component :is="iconMap[f.icon]" /></el-icon>
          </div>
          <div class="feature-text">
            <strong>{{ f.title }}</strong>
            <span>{{ f.desc }}</span>
          </div>
        </div>
      </div>

      <div class="feature-quote">
        <p>{{ currentSlogan.text }}</p>
        <span>-- {{ currentSlogan.author }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, reactive, onMounted, onUnmounted} from 'vue';
import {useRouter} from 'vue-router';
import {ElMessage} from 'element-plus';
import {View, Hide, Connection, Flag, User, DataAnalysis, Setting} from '@element-plus/icons-vue';
import {userAPI} from "@/features/user/api/user";
import {authAPI} from "@/features/auth/api/auth";
import AnimatedCharacters from "@/features/auth/components/animated-characters/AnimatedCharacters.vue";
import {startLoginSession} from "@/core/utils/loginSession";

const iconMap = { Connection, Flag, User, DataAnalysis, Setting };

const features = [
  { icon: 'Connection', title: '刷题同步', desc: '自动同步 CF · 洛谷提交和过题记录' },
  { icon: 'Flag', title: '任务分配', desc: '集训队/技术组任务领取与进度跟踪' },
  { icon: 'User', title: '团队管理', desc: '团队信息 · 成员 · 训练赛报名' },
  { icon: 'DataAnalysis', title: '训练统计', desc: '刷题排行 · 个人训练量 · 未通过题' },
  { icon: 'Setting', title: 'OJ 绑定', desc: '一站式绑定各平台 OJ 账号' },
];

const isTyping = ref(false);
const showPassword = ref(false);
const passwordLength = ref(0);
const onPasswordInput = (val) => {
  isTyping.value = !!val && val.length > 0;
  passwordLength.value = (val || '').length;
};

const slogans = [
  {text: '世界新生伊始，许多事物还没有名字，提到的时候尚需用手指指点点。', author: '《百年孤独》'}
];
const currentSlogan = ref(slogans[0]);
let sloganInterval = null;
onMounted(() => {
  sloganInterval = setInterval(() => {
    const idx = slogans.findIndex(s => s.text === currentSlogan.value.text);
    currentSlogan.value = slogans[(idx + 1) % slogans.length];
  }, 7000);
});
onUnmounted(() => { if (sloganInterval) clearInterval(sloganInterval); });

const router = useRouter();
const loading = ref(false);
const activeTab = ref('password');
const rememberMe = ref(!!localStorage.getItem('rememberUsername'));
const passwordForm = reactive({
  username: localStorage.getItem('rememberUsername') || '',
  password: '',
  grantType: "password"
});
const totpForm = reactive({username: '', code: '', grantType: "code"});

const handleLoginSuccess = async (loginData) => {
  startLoginSession();
  localStorage.setItem("accessToken", loginData.accessToken);
  localStorage.setItem("refreshToken", loginData.refreshToken);
  localStorage.setItem("isLogin", 'true');
  const username = passwordForm.username || totpForm.username;
  localStorage.setItem("user", username);
  if (rememberMe.value) localStorage.setItem('rememberUsername', username);
  else localStorage.removeItem('rememberUsername');
  try {
    const userInfoRes = await userAPI.getUserInfo();
    if (userInfoRes.data) localStorage.setItem("userInfo", JSON.stringify(userInfoRes.data));
  } catch (error) { console.error('获取用户信息失败:', error); }
  router.push({name: 'HomePage'});
};

const loginWithPassword = async () => {
  if (!passwordForm.username || !passwordForm.password) { ElMessage.error('请输入账号和密码'); return; }
  loading.value = true;
  try {
    const res = await authAPI.login(passwordForm);
    if (res.success) handleLoginSuccess(res.data);
    else ElMessage.error(res.msg || '账号或密码错误');
  } catch (error) { ElMessage.error('登录失败，请稍后重试'); }
  finally { loading.value = false; }
};

const loginWithTotp = async () => {
  if (!totpForm.username || !totpForm.code) { ElMessage.error('请输入账号和动态码'); return; }
  loading.value = true;
  try {
    const res = await authAPI.login(totpForm);
    if (res.success) handleLoginSuccess(res.data);
    else ElMessage.error(res.msg || '账号或动态码错误');
  } catch (error) { ElMessage.error('登录失败，请稍后重试'); }
  finally { loading.value = false; }
};
</script>

<style scoped>
.login-body { min-height: 100vh; width: 100vw; display: flex; }

/* ============ 左侧 ============ */
.login-visual {
  width: 30%; background: var(--accent-dark, #114d58);
  display: flex; flex-direction: column; justify-content: space-between;
  align-items: center; padding: 40px 24px; overflow: hidden; position: relative;
}
.decor-blur { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; z-index: 0; }
.decor-blur--1 { top: 10%; right: 0; width: 240px; height: 240px; background: rgba(15, 124, 130, 0.35); }
.decor-blur--2 { bottom: 5%; left: 0; width: 300px; height: 300px; background: rgba(10, 61, 78, 0.4); }

.visual-top { position: relative; z-index: 2; text-align: center; }
.visual-brand { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 12px; }
.brand-dot { width: 10px; height: 10px; border-radius: 50%; background: var(--accent, #0f7c82); box-shadow: 0 0 8px rgba(15, 124, 130, 0.5); }
.visual-brand-name { font-size: 22px; font-weight: 700; color: #fff; letter-spacing: 1px; }
.visual-poem { font-size: 16px; color: rgba(255, 255, 255, 0.7); font-style: italic; line-height: 1.6; margin: 0; }

.visual-characters { display: flex; align-items: flex-end; justify-content: center; position: relative; z-index: 2; }
.visual-footer { position: relative; z-index: 2; font-size: 12px; color: rgba(255, 255, 255, 0.35); margin: 0; }

/* ============ 中间 ============ */
.login-form-side {
  width: 40%; background: var(--surface, #fff);
  display: flex; align-items: center; justify-content: center; padding: 48px; position: relative;
}
.form-wrapper { max-width: 360px; width: 100%; }
.form-header { margin-bottom: 28px; }
.form-title { font-size: 26px; font-weight: 700; color: var(--text-main, #18343d); margin: 0 0 8px; letter-spacing: -0.02em; }
.form-subtitle { font-size: 14px; color: var(--text-soft, #627b83); margin: 0; line-height: 1.5; }
.mode-switch { display: flex; gap: 24px; margin-bottom: 24px; border-bottom: 1px solid var(--line-light, rgba(16, 74, 88, 0.08)); }
.mode-btn { background: none; border: none; font-size: 14px; font-weight: 500; color: var(--text-muted, #909399); cursor: pointer; padding: 0 0 10px; position: relative; transition: color 0.2s; }
.mode-btn.active { color: var(--accent, #0f7c82); font-weight: 600; }
.mode-btn.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 2px; background: var(--accent, #0f7c82); }
.login-form { display: flex; flex-direction: column; gap: 18px; }
.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 13px; font-weight: 500; color: var(--text-main, #18343d); }
.form-options { display: flex; align-items: center; }
.pwd-toggle { cursor: pointer; color: var(--text-muted, #909399); transition: color 0.2s; }
.pwd-toggle:hover { color: var(--accent, #0f7c82); }
.submit-btn { width: 100%; margin-top: 4px; height: 48px; font-size: 15px; font-weight: 600; letter-spacing: 1px; }
.register-entry { text-align: center; font-size: 13px; color: var(--text-muted, #909399); margin-top: 24px; }
.register-entry a { color: var(--accent, #0f7c82); font-weight: 500; margin-left: 4px; }
.register-entry a:hover { text-decoration: underline; }

/* ============ 右侧 ============ */
.login-features {
  width: 30%; background: var(--page-bg, #eef3f6);
  display: flex; flex-direction: column; justify-content: center; padding: 40px 28px; gap: 24px;
}

.features-header { }
.features-title { font-size: 18px; font-weight: 700; color: var(--text-main, #18343d); margin: 0 0 4px; }
.features-subtitle { font-size: 13px; color: var(--text-soft, #627b83); margin: 0; }

.feature-grid { display: flex; flex-direction: column; gap: 10px; }

.feature-card {
  display: flex; align-items: flex-start; gap: 14px;
  background: var(--surface, #fff); border-radius: 12px; padding: 16px;
  transition: all 0.25s ease; cursor: default;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(17, 77, 88, 0.12);
}

.feature-icon-box {
  width: 40px; height: 40px; border-radius: 8px;
  background: var(--accent-light, rgba(15, 124, 130, 0.08));
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background 0.25s ease;
}
.feature-card:hover .feature-icon-box {
  background: var(--accent, #0f7c82);
}
.feature-icon { font-size: 18px; color: var(--accent, #0f7c82); transition: color 0.25s ease; }
.feature-card:hover .feature-icon { color: #fff; }

.feature-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.feature-text strong { font-size: 14px; font-weight: 600; color: var(--text-main, #18343d); }
.feature-text span { font-size: 12px; color: var(--text-soft, #627b83); line-height: 1.4; }

/* 引用 */
.feature-quote {
  padding: 14px 16px; background: rgba(255, 255, 255, 0.6);
  border-radius: 10px; border-left: 3px solid var(--accent, #0f7c82);
  font-size: 12px; color: var(--text-soft, #627b83); line-height: 1.5;
}
.feature-quote p { margin: 0 0 4px; font-style: italic; }
.feature-quote span { font-size: 11px; opacity: 0.7; }

/* 响应式 */
@media (max-width: 1100px) { .login-features { display: none; } .login-visual { width: 40%; } .login-form-side { width: 60%; } }
@media (max-width: 820px) { .login-visual { display: none; } .login-form-side { width: 100%; } }
</style>
